/**
 * SetterNode - Node component for 'setter' type fields
 * 
 * Displays variable setter with operation and value.
 * Allows setting, incrementing, or decrementing variable values.
 */

import React, { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import IconProvider from '@/components/ui/IconProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/DefaultSelectDropdown';
import { DialogField } from '@/lib/stores/dialogStore';
import { BaseDialogNode } from './BaseDialogNode';
import { DialogFieldNodeData, getFieldTypeIcon, getFieldTypeColor } from './dialogNodeUtils';

export const SetterNode = memo(({ id, data, selected }: NodeProps<any>) => {
  const nodeData = data as DialogFieldNodeData;
  const field = nodeData.field;
  
  if (!field) {
    return null;
  }
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedType, setEditedType] = useState(field.type);
  const [editedVariable, setEditedVariable] = useState(field.setter?.variable || '');
  const [editedOperator, setEditedOperator] = useState<'set' | 'increment' | 'decrement'>(field.setter?.operator || 'set');
  const [editedValue, setEditedValue] = useState<boolean | number | string>(field.setter?.value ?? 0);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const prevSelectedRef = React.useRef(selected);

  // Get the selected variable's type (for edit mode)
  const selectedVariable = nodeData.variables?.find(v => v.name === editedVariable);
  const selectedVariableType = selectedVariable?.type;
  const isBoolean = selectedVariableType === 'boolean';
  const isText = selectedVariableType === 'text';
  const isRange = selectedVariableType === 'range';
  const isNumber = selectedVariableType === 'number' || isRange;
  
  // Get the variable type for view mode (based on saved setter)
  const viewVariable = nodeData.variables?.find(v => v.name === field.setter?.variable);
  const viewVariableType = viewVariable?.type;
  const viewIsBoolean = viewVariableType === 'boolean';
  const viewIsText = viewVariableType === 'text';

  // Attach native event listeners to prevent dragging on all inputs and selects
  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isEditing) return;

    const handlePointerDown = (e: PointerEvent) => {
      // Only prevent if target is an input, select, or textarea element
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' || 
          target.closest('input') || target.closest('select') || target.closest('textarea') ||
          target.closest('[role="combobox"]')) {
        e.stopImmediatePropagation();
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Only prevent if target is an input, select, or textarea element
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' || 
          target.closest('input') || target.closest('select') || target.closest('textarea') ||
          target.closest('[role="combobox"]')) {
        e.stopImmediatePropagation();
      }
    };

    // Use capture phase to run before React Flow's listeners
    content.addEventListener('pointerdown', handlePointerDown, { capture: true });
    content.addEventListener('mousedown', handleMouseDown, { capture: true });
    
    return () => {
      content.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      content.removeEventListener('mousedown', handleMouseDown, { capture: true });
    };
  }, [isEditing]);

  // Sync local state with prop changes when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditedType(field.type);
      setEditedVariable(field.setter?.variable || '');
      setEditedOperator(field.setter?.operator || 'set');
      setEditedValue(field.setter?.value ?? 0);
    }
  }, [field, isEditing]);

  // When variable changes, reset to default values
  const handleVariableChange = useCallback((newVariable: string) => {
    setEditedVariable(newVariable);
    const variable = nodeData.variables?.find(v => v.name === newVariable);
    const variableType = variable?.type;
    
    // Set default value based on type
    const defaultValue = variableType === 'boolean' ? false : 
                        variableType === 'text' ? '' : 0;
    setEditedValue(defaultValue);
    setEditedOperator('set');
  }, [nodeData.variables]);

  const handleSaveChanges = useCallback(() => {
    if (nodeData.onFieldUpdate) {
      const updates: Partial<DialogField> = {
        type: editedType,
      };
      
      // Include setter if we have a variable selected
      if (editedVariable) {
        updates.setter = {
          variable: editedVariable,
          operator: editedOperator,
          value: editedValue
        };
      }
      
      nodeData.onFieldUpdate(nodeData.nodeIndex, nodeData.fieldIndex, updates);
    }
    setIsEditing(false);
  }, [nodeData, editedType, editedVariable, editedOperator, editedValue]);

  // Auto-save when clicking outside
  useEffect(() => {
    // Only auto-save if we're transitioning from selected to unselected while editing
    if (prevSelectedRef.current && !selected && isEditing) {
      handleSaveChanges();
    }
    // Update ref for next render
    prevSelectedRef.current = selected;
  }, [selected, isEditing, handleSaveChanges]);

  const handleEditToggle = useCallback(() => {
    if (isEditing) {
      handleSaveChanges();
    } else {
      setIsEditing(true);
      setEditedType(field.type);
      setEditedVariable(field.setter?.variable || '');
      setEditedOperator(field.setter?.operator || 'set');
      setEditedValue(field.setter?.value ?? 0);
    }
  }, [isEditing, handleSaveChanges, field]);

  const colors = getFieldTypeColor(isEditing ? editedType : field.type);

  // Header content for edit mode (with type selector)
  const headerContent = isEditing ? (
    <>
      <IconProvider iconstring={getFieldTypeIcon(editedType)} size={14} className={colors.text} />
      <Select value={editedType} onValueChange={(value: any) => setEditedType(value)}>
        <SelectTrigger className="h-6 text-xs flex-1 bg-white noDrag">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="dialog">Dialog</SelectItem>
          <SelectItem value="question">Question</SelectItem>
          <SelectItem value="context">Context</SelectItem>
          <SelectItem value="condition">Condition</SelectItem>
          <SelectItem value="setter">Setter</SelectItem>
          <SelectItem value="end-dialog">End Dialog</SelectItem>
        </SelectContent>
      </Select>
    </>
  ) : undefined;

  return (
    <BaseDialogNode
      id={id}
      data={nodeData}
      selected={selected}
      isEditing={isEditing}
      onEditToggle={handleEditToggle}
      headerContent={headerContent}
    >
      <div ref={contentRef}>
        {/* Setter configuration editing - when in edit mode */}
        {isEditing && (
        <div className="mb-2">
          {/* Variable selector */}
          <div className="flex items-center gap-2 mb-2 p-2 bg-green-50 border border-green-200 rounded">
            <IconProvider iconstring="fa-code" size={12} className="text-green-700 flex-shrink-0" />
            <span className="text-xs font-semibold text-green-800 flex-shrink-0">Variable:</span>
            {nodeData.variables && nodeData.variables.length > 0 ? (
              <Select value={editedVariable} onValueChange={handleVariableChange}>
                <SelectTrigger className="h-6 text-xs flex-1 bg-white font-mono noDrag">
                  <SelectValue placeholder="Select variable..." />
                </SelectTrigger>
                <SelectContent>
                  {nodeData.variables.map((variable) => (
                    <SelectItem key={variable.name} value={variable.name}>
                      <span className="font-mono">{variable.name}</span>
                      <span className="text-muted-foreground ml-2">({variable.type})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-xs text-gray-500 italic">No variables defined in dialog</span>
            )}
          </div>
          
          {/* Operation and Value configuration */}
          {editedVariable && (
            <div className="space-y-2">
              {/* Operation selector (hidden for boolean and text) */}
              {!isBoolean && !isText && (
                <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                  <span className="text-xs font-semibold text-green-800 flex-shrink-0">Operation:</span>
                  <Select value={editedOperator} onValueChange={(value: any) => setEditedOperator(value)}>
                    <SelectTrigger className="h-6 text-xs flex-1 bg-white noDrag">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="set">Set</SelectItem>
                      <SelectItem value="increment">Increment</SelectItem>
                      <SelectItem value="decrement">Decrement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Value input */}
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                <span className="text-xs font-semibold text-green-800 flex-shrink-0">Value:</span>
                {isBoolean ? (
                  <Select value={String(editedValue)} onValueChange={(value) => setEditedValue(value === 'true')}>
                    <SelectTrigger className="h-6 text-xs flex-1 bg-white noDrag">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">true</SelectItem>
                      <SelectItem value="false">false</SelectItem>
                    </SelectContent>
                  </Select>
                ) : isText ? (
                  <Input
                    type="text"
                    value={String(editedValue)}
                    onChange={(e) => setEditedValue(e.target.value)}
                    className="h-6 text-xs flex-1 noDrag"
                    placeholder="Text value"
                  />
                ) : (
                  <Input
                    type="number"
                    value={String(editedValue)}
                    onChange={(e) => setEditedValue(parseFloat(e.target.value) || 0)}
                    className="h-6 text-xs flex-1 noDrag"
                    placeholder="Number"
                    min={isRange ? selectedVariable?.rangeMin : undefined}
                    max={isRange ? selectedVariable?.rangeMax : undefined}
                    step={isRange ? selectedVariable?.increment : undefined}
                  />
                )}
              </div>
              
              {/* Range info */}
              {isRange && selectedVariable && (
                <div className="text-xs text-gray-500 italic px-2">
                  Range: {selectedVariable.rangeMin ?? 0} to {selectedVariable.rangeMax ?? 100}, step: {selectedVariable.increment ?? 1}
                </div>
              )}

              {/* Operation hint */}
              {!isText && !isBoolean && editedOperator !== 'set' && (
                <div className="text-xs text-gray-600 italic px-2">
                  {editedOperator === 'increment' && `Will add ${editedValue} to ${editedVariable}`}
                  {editedOperator === 'decrement' && `Will subtract ${editedValue} from ${editedVariable}`}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {/* View mode - Display current setter configuration */}
        {!isEditing && field.setter?.variable && (
          <div className="text-sm space-y-1">
            <div className="flex items-center gap-2">
              <IconProvider iconstring="fa-code" size={12} className="text-green-700" />
              <span className="font-mono font-semibold text-green-800">{field.setter.variable}</span>
            </div>
            <div className="text-xs text-gray-600 pl-5">
              {field.setter.operator === 'set' && (
                <>Set to: <span className="font-mono font-semibold">{String(field.setter.value)}</span></>
              )}
              {field.setter.operator === 'increment' && (
                <>Add: <span className="font-mono font-semibold">{String(field.setter.value)}</span></>
              )}
              {field.setter.operator === 'decrement' && (
                <>Subtract: <span className="font-mono font-semibold">{String(field.setter.value)}</span></>
              )}
              {viewIsBoolean && ` (boolean)`}
              {viewIsText && ` (text)`}
              {!viewIsBoolean && !viewIsText && ` (number)`}
            </div>
          </div>
        )}

        {/* No setter configured */}
        {!isEditing && !field.setter?.variable && (
          <div className="text-xs text-gray-500 italic">
            No variable selected
          </div>
        )}

        {/* Single source handle for next connection */}
        <Handle
          type="source"
          position={Position.Right}
          id="next"
          className="w-3 h-3 border-2 border-green-600 bg-green-100"
          style={{ top: '50%' }}
        />
      </div>
    </BaseDialogNode>
  );
});
